import { hlvVanillaMigrationIsActive, hlvVanillaPrereleaseUrlIsActive } from 'services/statsig/featureGates';

const IDENTITY_BASE_URL = process.env.REACT_APP_IDENTITY_BASE_URL;
const getVanillaHlvPathPrefix = (): '/forums' | '/new-hlv-forums' =>
  hlvVanillaPrereleaseUrlIsActive() ? '/new-hlv-forums' : '/forums';

const getVanillaHlvPath = (path: string): string => `${getVanillaHlvPathPrefix()}${path}`;

export const getForumsHomeUrl = (): string =>
  hlvVanillaMigrationIsActive() ? getVanillaHlvPathPrefix() : (process.env.FORUMS_BASE_URL as string);

export const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url);

export const toAbsoluteCommunityUrl = (url: string): string =>
  isAbsoluteUrl(url) ? url : `${process.env.HOME_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;

type AuthQueryParamValues = {
  email?: string;
  redirectUrl?: string;
  prompt?: string;
  experience?: string;
};

const buildAuthQueryParams = (
  urlType: 'identity' | 'forums',
  { email, redirectUrl, prompt, experience = 'entry-auth' }: AuthQueryParamValues,
) => {
  const queryParams = [];

  if (email) {
    queryParams.push(`login_hint=${email}`);
  }
  if (experience) {
    // For 'community' experience, don't add the suffix. This is for account switching.
    const applicationValue = experience === 'community' ? 'community' : `community--${experience}`;
    queryParams.push(`application=${applicationValue}`);
  }
  if (redirectUrl) {
    queryParams.push(urlType === 'identity' ? `continue=${redirectUrl}` : `referer=${redirectUrl}`);
  }
  if (prompt) {
    queryParams.push(`prompt=${prompt}`);
  }
  return queryParams;
};

export const getAuthUrl = (type: 'login' | 'signup' | 'logout', queryParamValues: AuthQueryParamValues): string => {
  const queryParams = buildAuthQueryParams('identity', queryParamValues);
  return `${IDENTITY_BASE_URL}/${type}?${queryParams.join('&')}`;
};

// Forums URL generators — gated by community-forums-hlv-vanilla (see featureGates.ts).
export const getForumsSignInUrl = (queryParamValues: AuthQueryParamValues): string => {
  const queryParams = [];
  if (hlvVanillaMigrationIsActive()) {
    if (queryParamValues.redirectUrl) {
      queryParams.push(`Target=${encodeURIComponent(queryParamValues.redirectUrl)}`);
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return getVanillaHlvPath(`/entry/signin${queryString}`);
  }
  if (queryParamValues.redirectUrl) {
    queryParams.push(`referer=${queryParamValues.redirectUrl}`);
  }
  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  return `/forums/s/plugins/common/feature/oauth2sso_v2/sso_login_redirect${queryString}`;
};

export const getForumsSignOutUrl = (khorosEndpointPath: string, redirectUrl?: string): string => {
  if (hlvVanillaMigrationIsActive()) {
    const queryString = redirectUrl ? `?Target=${encodeURIComponent(redirectUrl)}` : '';
    return getVanillaHlvPath(`/entry/signout${queryString}`);
  }
  return `forums/s/plugins/custom/atlassian/${khorosEndpointPath}/khoros_logout`;
};

export const getForumsRegisterUrl = (queryParamValues: AuthQueryParamValues): string => {
  const queryParams = [];
  if (hlvVanillaMigrationIsActive()) {
    if (queryParamValues.redirectUrl) {
      queryParams.push(`Target=${encodeURIComponent(queryParamValues.redirectUrl)}`);
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return getVanillaHlvPath(`/entry/register${queryString}`);
  }

  return getAuthUrl('signup', {
    redirectUrl: queryParamValues.redirectUrl,
    experience: queryParamValues.experience,
    email: queryParamValues.email,
  });
};

/**
 * TEMPORARY (pre-CloudFront `/forums` migration):
 *
 * Higher Logic Vanilla (HLV) currently lives on a separate origin, so its post-consent
 * The prerelease HLV production URL is served under `community.atlassian.com/new-hlv-forums`,
 * while dev/staging may still use Vanilla vendor hosts. This helper recognises those valid
 * HLV return URLs while rejecting anything else to avoid open redirects.
 *
 * Matches HLV return URLs, restricted to the entry callback path:
 *   - dev:             https://community.dev.atlassian.com/new-hlv-forums/entry/...
 *   - stg:             https://community.stg.atlassian.com/new-hlv-forums/entry/...
 *   - prod prerelease: https://community.atlassian.com/new-hlv-forums/entry/...
 *   - legacy prod prerelease fallback: https://atlassian.vanillacommunities.com/entry/...
 *
 * @param redirectParam the raw `redirect` query param value
 * @returns the URL if it is a valid HLV return URL, otherwise null
 */
const HLV_RETURN_URL_PATTERN =
  /^https:\/\/(?:(?:community(?:\.dev|\.stg)?\.atlassian\.com\/new-hlv-forums|atlassian\.vanillacommunities\.com)\/entry\/)/i;

export const getAllowedExternalRedirect = (redirectParam: string | null | undefined): string | null => {
  if (!redirectParam) {
    return null;
  }
  return HLV_RETURN_URL_PATTERN.test(redirectParam) ? redirectParam : null;
};
